import React, { useState, useEffect } from 'react';
import DashboardLayout from '../layout/DashboardLayout';
import Table from '../dashboard/Table';
import DataDisplay from '../dashboard/DataDisplay';
import DashboardHeader from '../dashboard/DashboardHeader';
import TableControls from '../dashboard/TableControls';
import { useDispatch, useSelector } from 'react-redux';
import { fetchClients, loadColumnPreferences, saveColumnPreferences, updateClient, deleteClient, clearAssociationError } from '../../store/slices/clientsSlice';
import { setSearchLoading } from '../../store/slices/searchSlice';
import { closeGreyMenu } from '../../store/slices/menuSlice';
import Spinner from '../common/Spinner';
import ClientDetailsDialog from './ClientDetailsDialog';
import CustomizeFieldsDialog from '../dashboard/CustomizeFieldsDialog';
import NewClient from './NewClient';
import { DocumentIcon } from '@heroicons/react/24/outline';
import { showToast } from '../utils/Toast';
import { useThemeColors } from '../../hooks/useThemeColors';

function Clients() {
  const dispatch = useDispatch();
  const { buttonColors } = useThemeColors();
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchColumn, setSearchColumn] = useState('all');
  const [isCustomizeFieldsOpen, setIsCustomizeFieldsOpen] = useState(false);
  const [isNewClientOpen, setIsNewClientOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isClientDetailsOpen, setIsClientDetailsOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isAssociationErrorDialogOpen, setIsAssociationErrorDialogOpen] = useState(false);
  
  const { clients, loading, pagination, columnPreferences, associationError } = useSelector(state => state.clients);

  // Ensure GreyMenu is closed when Clients component mounts
  useEffect(() => {
    dispatch(closeGreyMenu());
  }, [dispatch]);

  // Define default columns
  const defaultColumns = [
    { id: 1, key: 'name', label: 'Name', name: 'Name', active: true, checked: true },
    { id: 2, key: 'email', label: 'Email', name: 'Email', active: true, checked: true },
    { id: 3, key: 'cellPhone', label: 'Cell Phone', name: 'Cell Phone', active: true, checked: true },
    { id: 4, key: 'altPhone', label: 'Alt Phone', name: 'Alt Phone', active: true, checked: false },
    { id: 5, key: 'address.street', label: 'Street', name: 'Street', active: true, checked: false },
    { id: 6, key: 'address.city', label: 'City', name: 'City', active: true, checked: true },
    { id: 7, key: 'address.state', label: 'State', name: 'State', active: true, checked: true },
    { id: 8, key: 'address.zipCode', label: 'Zip Code', name: 'Zip Code', active: true, checked: true },
    { id: 9, key: 'mapLink', label: 'Map Link', name: 'Map Link', active: true, checked: false },
    { id: 10, key: 'createdAt', label: 'Created Date', name: 'Created Date', active: true, checked: true },
    { id: 11, key: 'updatedAt', label: 'Last Modified', name: 'Last Modified', active: true, checked: true },
  ];

  // State for columns with default values
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [activeColumns, setActiveColumns] = useState(defaultColumns);

  // Load clients when component mounts or when dependencies change
  useEffect(() => {
    loadClients();
  }, [currentPage, sortConfig, searchTerm, searchColumn, activeTab, dispatch]);

  // Load preferences and set columns on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const result = await dispatch(loadColumnPreferences()).unwrap();
        if (result?.clientColumns?.length) {
          // Merge saved preferences with default columns
          const mergedColumns = defaultColumns.map(col => {
            const savedPref = result.clientColumns.find(pref => parseInt(pref.id) === col.id);
            if (savedPref) {
              return {
                ...col,
                key: col.key,
                label: col.label,
                checked: savedPref.checked,
                active: savedPref.active
              };
            }
            return col;
          });
          setActiveColumns(mergedColumns);
          setVisibleColumns(mergedColumns.filter(col => col.checked));
        } else {
          // Use default columns if no preferences found
          setVisibleColumns(defaultColumns.filter(col => col.checked));
        }
      } catch (error) {
        console.error('Failed to load preferences:', error);
        setVisibleColumns(defaultColumns.filter(col => col.checked));
      }
    };
    loadPreferences();
  }, [dispatch]);

  // Show association error dialog when error exists
  useEffect(() => {
    if (associationError) {
      setIsAssociationErrorDialogOpen(true);
    }
  }, [associationError]);

  const loadClients = async () => {
    const params = {
      page: currentPage,
      limit: 10,
      ...(sortConfig.key && {
        sortField: sortConfig.key,
        sortOrder: sortConfig.direction
      }),
      ...(searchTerm && { 
        search: searchTerm,
        ...(searchColumn !== 'all' && { searchField: searchColumn })
      })
    };

    // Add tab-specific parameters
    switch (activeTab) {
      case 'recent':
        params.sortField = 'createdAt';
        params.sortOrder = 'desc';
        break;
      case 'all':
      default:
        break;
    }

    return dispatch(fetchClients(params)).unwrap();
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleColumnToggle = (columnId) => {
    const updatedColumns = activeColumns.map(col => {
      if (col.id === columnId) {
        return { ...col, checked: !col.checked };
      }
      return col;
    });
    setActiveColumns(updatedColumns);
  };

  const handleApplyColumns = async (selectedColumns) => {
    try {
      // Ensure columns have all required properties
      const columnsWithKeys = selectedColumns.map(col => {
        const originalCol = defaultColumns.find(defaultCol => defaultCol.id === parseInt(col.id));
        return {
          ...col,
          key: originalCol?.key || col.key,
          label: originalCol?.label || col.label,
          name: originalCol?.name || col.name
        };
      });

      // Make sure we're sending an array of columns
      await dispatch(saveColumnPreferences({
        columns: Array.isArray(columnsWithKeys) ? columnsWithKeys : [],
        type: 'clients'
      })).unwrap();
      
      const updatedColumns = columnsWithKeys.filter(col => col.checked);
      setVisibleColumns(updatedColumns);
      setActiveColumns(columnsWithKeys);
      setIsCustomizeFieldsOpen(false);
    } catch (error) {
      console.error('Failed to save preferences:', error);
    }
  };

  // Transform client data based on visible columns
  const transformClientData = (client) => {
    const transformed = {};
    visibleColumns.forEach(column => {
      if (!column.key) {
        console.warn('Column missing key property:', column);
        return;
      }

      // Handle nested properties
      const value = column.key.split('.').reduce((obj, key) => {
        if (obj === null || obj === undefined) return '';
        return obj[key];
      }, client);
      
      // Special handling for email to display multiple emails nicely
      if (column.key === 'email' && value) {
        // Split by commas and create a formatted display
        const emails = value.split(',').map(email => email.trim()).filter(Boolean);
        if (emails.length > 1) {
          transformed[column.key] = (
            `${emails[0]} +${emails.length - 1} more`
          );
        } else {
          transformed[column.key] = value || 'N/A';
        }
      } else {
        transformed[column.key] = value || 'N/A';
      }
    });
    transformed.id = client._id;
    
    // Format dates
    if (transformed.createdAt) {
      transformed.createdAt = new Date(transformed.createdAt).toLocaleString('en-US', {
        timeZone: 'America/Los_Angeles',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    }
    if (transformed.updatedAt) {
      transformed.updatedAt = new Date(transformed.updatedAt).toLocaleString('en-US', {
        timeZone: 'America/Los_Angeles',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    }
    
    return transformed;
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setCurrentPage(1); // Reset to first page when changing tabs
    setSortConfig({ key: null, direction: 'asc' }); // Reset sort when changing tabs
  };

  const handleCheckRow = (id) => {
    setSelectedClients(prev => {
      if (prev.includes(id)) {
        return prev.filter(clientId => clientId !== id);
      }
      return [...prev, id];
    });
  };

  const handleCheckAll = () => {
    if (selectedClients.length === clients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(clients.map(client => client._id));
    }
  };

  const handleCellUpdate = async (clientId, field, value) => {
    try {
      // For nested fields like 'address.city', we need to build a proper update object
      const updates = {};
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        updates[parent] = { [child]: value };
      } else {
        // Special handling for email field to validate multiple emails
        if (field === 'email' && value) {
          const emails = value.split(',').map(email => email.trim()).filter(Boolean);
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          const invalidEmails = emails.filter(email => !emailRegex.test(email));
          
          if (invalidEmails.length > 0) {
            showToast(`Invalid email(s): ${invalidEmails.join(', ')}`, 'error');
            return;
          }
        }
        updates[field] = value;
      }

      await dispatch(updateClient({ 
        id: clientId, 
        updates
      })).unwrap();
      
      // Refresh the clients data
      loadClients();
    } catch (error) {
      console.error('Failed to update client:', error);
    }
  };

  // Update search handler to manage loading state
  const handleSearch = async (searchValue, columnKey = 'all') => {
    try {
      dispatch(setSearchLoading(true));
      setSearchTerm(searchValue);
      setSearchColumn(columnKey);
      setCurrentPage(1); // Reset to first page when searching
      
      // Wait for the clients to load with the new search term
      await loadClients();
    } finally {
      dispatch(setSearchLoading(false));
    }
  };

  // Add handler for row clicks
  const handleRowClick = (row) => {
    // Find the full client data from the clients array
    const fullClient = clients.find(client => client._id === row.id);
    if (!fullClient) return;
    
    setSelectedClient(fullClient);
    setIsClientDetailsOpen(true);
  };

  // Only include the client-specific tabs we want to show
  const clientTabs = [
    { id: 'all', name: 'All Clients' },
    { id: 'recent', name: 'Recent' }
  ];

  const handleDeleteClient = async () => {
    if (selectedClients.length === 0) {
      showToast('Please select at least one client to delete', 'warning');
      return;
    }

    if (selectedClients.length > 1) {
      showToast('Please select only one client to delete at a time', 'warning');
      return;
    }

    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteClient = async () => {
    const clientId = selectedClients[0];
    
    try {
      await dispatch(deleteClient(clientId)).unwrap();
      setSelectedClients([]);
      showToast('Client deleted successfully', 'success');
    } catch (error) {
      console.error('Failed to delete client:', error);
      
      // If it's not an association error, show a generic error toast
      if (!error.hasAssociations) {
        showToast(error.message || 'Failed to delete client', 'error');
      }
      // Association errors are handled by the useEffect watching associationError
    } finally {
      setIsDeleteDialogOpen(false);
    }
  };

  const handleCloseAssociationDialog = () => {
    setIsAssociationErrorDialogOpen(false);
    dispatch(clearAssociationError());
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col h-screen">
        <DashboardHeader 
          title="Clients"
          hideCreateLead={false}
          onNewClick={() => setIsNewClientOpen(true)}
          onCustomizeFields={() => setIsCustomizeFieldsOpen(true)}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          selectedItems={selectedClients}
          onSearch={handleSearch}
          searchTerm={searchTerm}
          tabs={clientTabs}
          createButtonText="Create New Client"
          onDelete={handleDeleteClient}
          items={clients}
          hideActions={false}
          visibleColumns={visibleColumns}
        />
        <div className="flex-1 overflow-hidden p-4 flex flex-col">
          <div className="mb-4">
            <span className="text-lg text-gray-600">{pagination?.total || 0} Clients</span>
          </div>
          
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <Spinner size="xl" color="blue" />
            </div>
          ) : (
            <>
              {(!clients || clients.length === 0) ? (
                <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                  <DocumentIcon className="w-16 h-16 mb-4" />
                  <p className="text-lg font-medium">No clients found</p>
                  <p className="text-sm">Create a new client to get started</p>
                  <button 
                    onClick={() => setIsNewClientOpen(true)}
                    className="mt-4 px-4 py-2 text-white rounded-lg transition-colors"
                    style={{
                      backgroundColor: buttonColors.primary,
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = buttonColors.primaryHover}
                    onMouseLeave={(e) => e.target.style.backgroundColor = buttonColors.primary}
                  >
                    Add New Client
                  </button>
                </div>
              ) : (
                <div className="flex-1 min-h-0">
                  <DataDisplay 
                    columns={visibleColumns}
                    data={(clients || []).map(transformClientData)}
                    onSort={handleSort}
                    sortConfig={sortConfig}
                    onCheckAll={handleCheckAll}
                    onCheckRow={handleCheckRow}
                    selectedRows={selectedClients}
                    onUpdateCell={handleCellUpdate}
                    onRowClick={handleRowClick}
                    title="Clients"
                    pagination={{
                      total: pagination?.total || 0,
                      pages: pagination?.pages || 1,
                      currentPage: currentPage
                    }}
                    onPageChange={handlePageChange}
                    currentPage={currentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {isDeleteDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Delete Client
            </h3>
            <p className="text-gray-500 mb-6">
              Are you sure you want to delete this client? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-4">
              <button
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                onClick={() => setIsDeleteDialogOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg"
                onClick={confirmDeleteClient}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Association Error Dialog */}
      {isAssociationErrorDialogOpen && associationError && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Cannot Delete Client
            </h3>
            <p className="text-gray-500 mb-3">
              This client cannot be deleted because it has associated records:
            </p>
            <ul className="list-disc pl-5 mb-6 text-gray-600">
              {associationError.associationCounts.leads > 0 && (
                <li>Leads: {associationError.associationCounts.leads}</li>
              )}
              {associationError.associationCounts.projects > 0 && (
                <li>Projects: {associationError.associationCounts.projects}</li>
              )}
            </ul>
            <p className="text-gray-500 mb-6 italic">
              Please remove these associations before deleting this client.
            </p>
            <div className="flex justify-end">
              <button
                className="px-4 py-2 text-white rounded-lg transition-colors"
                style={{
                  backgroundColor: buttonColors.primary,
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = buttonColors.primaryHover}
                onMouseLeave={(e) => e.target.style.backgroundColor = buttonColors.primary}
                onClick={handleCloseAssociationDialog}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      <CustomizeFieldsDialog 
        isOpen={isCustomizeFieldsOpen}
        onClose={() => setIsCustomizeFieldsOpen(false)}
        columns={activeColumns}
        onToggleColumn={handleColumnToggle}
        onApply={handleApplyColumns}
        type="clients"
      />

      <NewClient 
        isOpen={isNewClientOpen}
        onClose={() => setIsNewClientOpen(false)}
      />

      <ClientDetailsDialog
        isOpen={isClientDetailsOpen}
        onClose={() => setIsClientDetailsOpen(false)}
        client={selectedClient}
      />
    </DashboardLayout>
  );
}

export default Clients; 